import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { ComposeBroadcastDialog } from '../components/dialogs/ComposeBroadcastDialog';
import { Pagination } from '../components/Pagination';
import { BroadcastsAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import type { Broadcast, DispatchStatus, Page } from '../types/api';
import { formatDateTime, formatCount } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';

const TERMINAL_STATUSES: DispatchStatus[] = ['SENT', 'PARTIAL', 'FAILED'];

export function BroadcastsPage() {
  const [composeOpen, setComposeOpen] = useState(false);
  const fetcher = useCallback(async ({ page }: { page: number }): Promise<Page<Broadcast>> => {
    return BroadcastsAPI.list(page);
  }, []);
  const q = usePagedQuery<Broadcast, Record<string, never>>({ fetcher, initialFilters: {} });

  // Auto-refresh while any visible row is still in flight (QUEUED / SENDING).
  const hasInFlight = useMemo(
    () => q.data.some(b => b.dispatchStatus && !TERMINAL_STATUSES.includes(b.dispatchStatus)),
    [q.data],
  );
  useEffect(() => {
    if (!hasInFlight) return;
    const t = setInterval(() => q.refresh(), 3000);
    return () => clearInterval(t);
  }, [hasInFlight, q]);

  return (
    <div>
      <PageHeader
        eyebrow="07 — BROADCASTS"
        title={<>The <em>broadside</em>.</>}
        lede="Push notifications via FCM to a chosen audience — everyone, subscribers (any), a specific user, or by country. Dispatch runs in the background; delivery counts refresh as Firebase confirms."
        actions={<Button tone="ember" onClick={() => setComposeOpen(true)}>+ Compose broadcast</Button>}
      />

      {q.error && <NotImplementedBanner endpoint="/api/admin/broadcasts" />}

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? <Card><EmptyState title="No broadcasts yet" hint="Send your first one with the button above." action={<Button tone="ember" onClick={() => setComposeOpen(true)}>+ Compose</Button>} /></Card> :
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr><th>Title</th><th>Audience</th><th>Sent</th><th>Delivered / Failed</th><th>Status</th><th>By</th></tr>
            </thead>
            <tbody>
              {q.data.map(b => (
                <tr key={b.id}>
                  <td>
                    <div className="nb-table__primary">{b.title}</div>
                    <div className="nb-table__sub">
                      {b.content.slice(0, 80)}{b.content.length > 80 ? '…' : ''}
                    </div>
                  </td>
                  <td><StatusPill tone="brass">{describeAudience(b)}</StatusPill></td>
                  <td className="nb-table__mono">{formatDateTime(b.sentAt)}</td>
                  <td className="nb-table__mono">
                    {formatCount(b.deliveredCount)} <span className="nb-table__sub">/ {formatCount(b.failedCount)}</span>
                  </td>
                  <td><DispatchPill status={b.dispatchStatus} /></td>
                  <td className="nb-table__mono">{b.sentBy || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      }

      <ComposeBroadcastDialog open={composeOpen} onClose={() => setComposeOpen(false)} onSent={() => { setComposeOpen(false); q.refresh(); }} />
    </div>
  );
}

function describeAudience(b: Broadcast): string {
  switch (b.audience) {
    case 'ALL':          return 'All users';
    case 'SUBSCRIBERS':  return 'Subscribers';
    case 'USER':         return `User · ${b.userEmail ? `email:${b.userEmail}` : `device:${b.userDeviceId ?? '—'}`}`;
    case 'COUNTRY':      return `Country · ${b.country ?? '—'}`;
    default:             return String(b.audience);
  }
}

function DispatchPill({ status }: { status?: DispatchStatus }) {
  if (!status)               return <StatusPill tone="brass">UNKNOWN</StatusPill>;
  if (status === 'SENT')     return <StatusPill tone="moss">SENT</StatusPill>;
  if (status === 'PARTIAL')  return <StatusPill tone="ember">PARTIAL</StatusPill>;
  if (status === 'FAILED')   return <StatusPill tone="rust">FAILED</StatusPill>;
  // QUEUED / SENDING
  return <StatusPill tone="brass">{status}</StatusPill>;
}
